import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import Logo from "../components/Logo";

export default function PublicProposal() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/proposals/public/${token}`).then((r) => setP(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#717680]">Proposal not found.</div>;
  if (!p) return <div className="min-h-screen flex items-center justify-center"><div className="w-2 h-10 bg-[#D90429] animate-pulse" /></div>;

  const subtotal = (p.items || []).reduce((s, i) => s + (Number(i.quantity) * Number(i.unit_price)), 0);
  const afterDisc = subtotal - Number(p.discount || 0);
  const tax = afterDisc * Number(p.tax_percent || 0) / 100;
  const total = afterDisc + tax;

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <header className="bg-white border-b border-black/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" />
          <a href={`${API}/proposals/public/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="public-pdf-download" className="inline-flex items-center gap-2 px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">
            <DownloadSimple size={16} weight="bold" /> Download PDF
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white border border-black/10">
          <div className="bg-[#D90429] text-white px-10 py-10">
            <div className="overline text-white/80">Proposal</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">{p.title}</h1>
            <div className="mt-3 text-white/90">{p.client_name} {p.client_company && `· ${p.client_company}`}</div>
          </div>

          <div className="px-10 py-10 space-y-8">
            {p.introduction && (<section><h2 className="font-display text-2xl font-bold tracking-tight mb-3">Introduction</h2><p className="whitespace-pre-wrap leading-relaxed text-[#0A0A0A]">{p.introduction}</p></section>)}
            {p.requirements && (<section><h2 className="font-display text-2xl font-bold tracking-tight mb-3">Requirements</h2><p className="whitespace-pre-wrap leading-relaxed">{p.requirements}</p></section>)}
            {p.scope && (<section><h2 className="font-display text-2xl font-bold tracking-tight mb-3">Scope of Work</h2><p className="whitespace-pre-wrap leading-relaxed">{p.scope}</p></section>)}

            {p.items?.length > 0 && (
              <section>
                <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Commercials</h2>
                <table className="w-full text-sm border border-black/10">
                  <thead className="bg-[#D90429] text-white"><tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Amount</th></tr></thead>
                  <tbody>
                    {p.items.map((it, i) => (<tr key={`${it.name}-${i}`} className="border-t border-black/5"><td className="px-3 py-2">{it.name}<div className="text-xs text-[#717680]">{it.description}</div></td><td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td><td className="px-3 py-2 text-right font-mono-shelv">₹{Number(it.unit_price).toLocaleString("en-IN")}</td><td className="px-3 py-2 text-right font-mono-shelv">₹{(Number(it.quantity) * Number(it.unit_price)).toLocaleString("en-IN")}</td></tr>))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[#717680]">Subtotal</td><td className="px-3 py-1.5 text-right font-mono-shelv">₹{subtotal.toLocaleString("en-IN")}</td></tr>
                    {Number(p.discount) > 0 && <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[#717680]">Discount</td><td className="px-3 py-1.5 text-right font-mono-shelv">-₹{Number(p.discount).toLocaleString("en-IN")}</td></tr>}
                    <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[#717680]">Tax ({p.tax_percent}%)</td><td className="px-3 py-1.5 text-right font-mono-shelv">₹{tax.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td></tr>
                    <tr className="bg-[#FFE5E9] font-bold"><td colSpan={3} className="px-3 py-2.5 text-right">GRAND TOTAL</td><td className="px-3 py-2.5 text-right font-mono-shelv text-[#D90429] text-lg">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td></tr>
                  </tfoot>
                </table>
              </section>
            )}

            {p.terms && (<section><h2 className="font-display text-2xl font-bold tracking-tight mb-3">Terms & Conditions</h2><p className="whitespace-pre-wrap leading-relaxed">{p.terms}</p></section>)}

            {p.items?.some((it) => it.brochure_path) && (
              <section data-testid="public-attachments">
                <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Attachments</h2>
                <ul className="space-y-2">
                  {p.items.filter((it) => it.brochure_path).map((it) => (
                    <li key={it.brochure_path}>
                      <a
                        href={`${API}/files/public/${it.brochure_path}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 border border-[#D90429]/30 bg-[#D90429]/5 hover:bg-[#D90429]/10 px-4 py-2 text-sm"
                        data-testid={`public-brochure-link-${it.brochure_path}`}
                      >
                        <span className="text-[#D90429]">📎</span>
                        <span className="font-semibold">{it.brochure_filename || `${it.name} - Brochure`}</span>
                        <span className="text-xs text-[#717680] ml-2">{it.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="border-t-2 border-[#D90429] pt-6">
              <div className="text-xs uppercase tracking-widest text-[#D90429] font-bold mb-2">Signed by</div>
              <div className="text-sm leading-relaxed text-[#0A0A0A]">
                Thanks &amp; Regards<br />
                <span className="font-bold">Mukesh Panchal</span><br />
                Shelves Tech Pvt. Ltd<br />
                Office No -110-111, 1st Floor<br />
                Gupta Arcade, Plot no-2<br />
                Inder Enclave, LSC Rohtak Road<br />
                Paschim Vihar New Delhi-110087<br />
                Mobile-9899730901 / 8920474021<br />
                GSTN-07AATCS6306H1Z0
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
